import * as recallApi from '@otter-ai/recall-client';
import { useMutation } from '@tanstack/react-query';
import type { InvitationDecodeType } from '@/shared/types/auth';
import { setPartialLoginState } from '@/shared/utils/auth';
import {
  SESSION_STORAGE_KEYS,
  setSessionItem,
} from '@/shared/utils/sessionStorageUtils';

export const useDecodeInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (res: InvitationDecodeType, token: string) => void;
  onError?: (error: Error) => void;
}) => {
  const {
    mutate: decodeInvitation,
    mutateAsync: decodeInvitationAsync,
    isPending: isDecodingInvitation,
  } = useMutation({
    mutationFn: async (token: string) => {
      const body = {
        token,
      };

      const response = await recallApi.invitationDecode({
        body,
        throwOnError: true,
      });

      return { res: response.data, token };
    },
    onSuccess: async ({ res, token }) => {
      setSessionItem(SESSION_STORAGE_KEYS.EMAIL_INPUT_STATE, res.email);
      setSessionItem(SESSION_STORAGE_KEYS.AUTH_EMAIL, res.email);
      setSessionItem(SESSION_STORAGE_KEYS.INVITER, res.inviter);
      setSessionItem(SESSION_STORAGE_KEYS.INVITATION_TOKEN, token);
      setSessionItem(SESSION_STORAGE_KEYS.WORKSPACE, res.workspace);
      setPartialLoginState({
        workspace: res.workspace,
        workspaceInvitationToken: token,
        isLogIn: res.user_email === true,
        isEmailInUse: res.user_email,
      });

      const passwordResetToken = res.password_reset_token;
      const isDirectorySynced = Boolean(
        res.is_directory_synced && !!res.password_reset_token,
      );

      if (isDirectorySynced) {
        setSessionItem(SESSION_STORAGE_KEYS.IS_DIRECTORY_SYNCED, true);
        setSessionItem(SESSION_STORAGE_KEYS.EMAIL_LOGIN, true);
      }
      if (passwordResetToken) {
        setSessionItem(
          SESSION_STORAGE_KEYS.PASSWORD_RESET_TOKEN,
          passwordResetToken,
        );
      }

      if (onSuccess) {
        onSuccess(res, token);
      }
    },
    onError: (error) => {
      if (onError) onError(error);
    },
  });

  return { decodeInvitation, decodeInvitationAsync, isDecodingInvitation };
};
